import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, providerImage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useCompare } from "@/context/CompareContext";
import {
  Star, ShieldCheck, X, Plus, Link2, GitCompare, Loader2, Search, ArrowRight, Crown,
} from "lucide-react";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString()}`);
const minPrice = (p) => p.min_price ?? (p.packages?.length ? Math.min(...p.packages.map((k) => k.price)) : null);

export default function Compare() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const compare = useCompare();
  const [providers, setProviders] = useState(null);
  const [allList, setAllList] = useState([]);
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);

  const ids = useMemo(() => (params.get("ids") || "").split(",").map((s) => s.trim()).filter(Boolean).slice(0, 3), [params]);

  useEffect(() => { document.title = "Compare Hospitals | SarvaSeva"; }, []);
  useEffect(() => { compare?.setMany(ids); }, [params]); // eslint-disable-line

  useEffect(() => {
    if (ids.length < 2) { setProviders([]); return; }
    setProviders(null);
    api.get(`/v1/providers/compare?ids=${ids.join(",")}`).then((r) => setProviders(r.data.providers)).catch(() => setProviders([]));
  }, [params]); // eslint-disable-line

  useEffect(() => {
    api.get("/v1/providers/map").then((r) => setAllList(r.data.providers)).catch(() => {});
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setAdding(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const goWith = (list) => navigate(`/compare?ids=${list.join(",")}`);
  const removeCol = (id) => goWith(ids.filter((x) => x !== id));
  const addCol = (id) => { setAdding(false); setQ(""); goWith([...ids, id]); };
  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Comparison link copied to clipboard");
  };
  const book = (p) => navigate(`/book?provider=${p.id}&specialty=${encodeURIComponent(p.specialties?.[0] || "")}`);

  const candidates = useMemo(
    () => allList.filter((p) => !ids.includes(p.id) && p.name.toLowerCase().includes(q.toLowerCase())).slice(0, 30),
    [allList, ids, q]);

  const bestPrice = useMemo(() => {
    const prices = (providers || []).map(minPrice).filter((x) => x != null);
    return prices.length ? Math.min(...prices) : null;
  }, [providers]);
  const bestRating = useMemo(() => Math.max(...((providers || []).map((p) => p.rating || 0)), 0), [providers]);
  const soleJci = useMemo(() => {
    const withJci = (providers || []).filter((p) => (p.accreditation || []).includes("JCI"));
    return withJci.length === 1 ? withJci[0].id : null;
  }, [providers]);

  if (ids.length < 2) return (
    <div className="min-h-screen bg-[#F7F9FC] grid place-items-center px-4 text-center" data-testid="compare-empty">
      <div>
        <GitCompare size={44} className="mx-auto text-[#98A2B3]" />
        <h1 className="font-heading font-bold text-2xl text-[#101828] mt-4">Compare Hospitals</h1>
        <p className="text-muted-foreground mt-2 max-w-md">Select at least 2 hospitals to compare them side by side. Tick the “Compare” box on any hospital card.</p>
        <Button className="mt-6 rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E]" onClick={() => navigate("/providers")} data-testid="compare-browse-btn">Browse Hospitals</Button>
      </div>
    </div>
  );

  if (!providers) return <div className="min-h-screen grid place-items-center bg-[#F7F9FC] text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  const rows = [
    { key: "city", label: "Location", render: (p) => <span>{p.city}{p.state ? `, ${p.state}` : ""}</span> },
    { key: "rating", label: "Rating", render: (p) => (
      <span className="inline-flex items-center gap-1 font-semibold">
        <Star size={15} fill={p.rating === bestRating ? "#D4A017" : "#D97706"} className={p.rating === bestRating ? "text-[#D4A017]" : "text-[#D97706]"} />
        {p.rating}{p.rating === bestRating && <Badge className="ml-1 bg-[#FDF0D5] text-[#8a6100] border-0 text-[10px]">Top rated</Badge>}
      </span>
    ) },
    { key: "accreditation", label: "Accreditation", render: (p) => (
      <div className="flex flex-wrap gap-1.5">
        {(p.accreditation || []).map((a) => (
          <Badge key={a} className={`border-0 text-xs ${a === "JCI" ? "bg-[#D4A017] text-white" : "bg-[#1D4ED8] text-white"}`}><ShieldCheck size={10} className="mr-1" />{a}</Badge>
        ))}
        {soleJci === p.id && <Badge className="bg-[#ECFDF5] text-[#087443] border-0 text-[10px]">JCI advantage</Badge>}
      </div>
    ) },
    { key: "capacity", label: "Capacity", render: (p) => (p.capacity ? `${p.capacity} intl. slots` : "—") },
    { key: "price", label: "Starting Price", render: (p) => {
      const mp = minPrice(p);
      const best = mp != null && mp === bestPrice;
      return <span className={`font-heading font-bold ${best ? "text-[#10B981]" : "text-[#0D5C75]"}`}>{money(mp)}{best && <Badge className="ml-1.5 bg-[#ECFDF5] text-[#087443] border-0 text-[10px]">Best value</Badge>}</span>;
    } },
    { key: "specialties", label: "Specialties", render: (p) => (
      <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
        {(p.specialties || []).map((s) => <Badge key={s} variant="outline" className="border-[#0D5C75]/25 bg-[#E9F5F9] text-[#0D5C75] text-[11px]">{s}</Badge>)}
      </div>
    ) },
    { key: "packages", label: "Top Packages", render: (p) => (
      <div className="space-y-1.5">
        {(p.packages || []).slice(0, 4).map((k) => (
          <div key={k.id} className="flex justify-between gap-2 text-xs"><span className="truncate">{k.name}</span><span className="font-semibold text-[#0D5C75] shrink-0">{money(k.price)}</span></div>
        ))}
        {!(p.packages || []).length && <span className="text-muted-foreground text-xs">—</span>}
      </div>
    ) },
    { key: "facilities", label: "Facilities", render: (p) => (
      <div className="flex flex-wrap gap-1.5">
        {(p.facilities || []).slice(0, 5).map((f) => <Badge key={f} variant="outline" className="border-[#E4E7EC] text-muted-foreground text-[11px]">{f}</Badge>)}
      </div>
    ) },
    { key: "website", label: "Website", render: (p) => p.website_url ? <a href={p.website_url} target="_blank" rel="noopener noreferrer" className="text-[#2A9D8F] hover:underline text-sm">Official site ↗</a> : "—" },
  ];

  const colWidth = "min-w-[240px] w-[280px]";

  return (
    <div className="bg-[#F7F9FC] min-h-screen" data-testid="compare-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-3xl font-extrabold text-[#101828] flex items-center gap-2"><GitCompare size={28} className="text-[#0D5C75]" /> Compare Hospitals</h1>
            <p className="text-sm text-muted-foreground mt-1">Side-by-side comparison of {providers.length} hospital{providers.length === 1 ? "" : "s"}.</p>
          </div>
          <Button variant="outline" className="rounded-full" onClick={copyLink} data-testid="compare-copy-link"><Link2 size={15} className="mr-1.5" /> Copy Link</Button>
        </div>

        <div className="overflow-x-auto pb-4 -mx-1 px-1" data-testid="compare-table">
          <div className="flex gap-4 min-w-min snap-x snap-mandatory">
            {/* Attribute label column (desktop) */}
            <div className="hidden lg:flex flex-col w-40 shrink-0 pt-[280px]">
              {rows.map((r) => (
                <div key={r.key} className="py-3 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b border-[#E4E7EC] min-h-[64px] flex items-center">{r.label}</div>
              ))}
              <div className="py-4 min-h-[64px]" />
            </div>

            {providers.map((p) => {
              const hero = (p.images || []).length ? providerImage(p.id, 0) : null;
              return (
                <div key={p.id} className={`${colWidth} shrink-0 snap-start rounded-2xl bg-white border border-[#E4E7EC] overflow-hidden flex flex-col`} data-testid={`compare-col-${p.id}`}>
                  {/* Sticky header */}
                  <div className="sticky top-0 z-10 bg-white">
                    <div className="relative h-40 bg-gradient-to-br from-[#0D5C75] to-[#2A9D8F]">
                      {hero && <img src={hero} alt={p.name} className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />}
                      <button onClick={() => removeCol(p.id)} data-testid={`compare-remove-${p.id}`}
                        className="absolute top-2 right-2 grid place-items-center h-7 w-7 rounded-full bg-black/40 hover:bg-black/60 text-white"><X size={15} /></button>
                      <div className="absolute top-2 left-2 flex gap-1">
                        {p.tier === "premium" && <Badge className="bg-[#FBBF24] text-[#101828] border-0 text-[10px]"><Crown size={10} className="mr-0.5" />Premium</Badge>}
                        {p.featured && <Badge className="bg-[#F97316] text-white border-0 text-[10px]">Featured</Badge>}
                      </div>
                    </div>
                    <div className="p-3 border-b border-[#E4E7EC] h-[100px] flex flex-col justify-center">
                      <div className="font-heading font-bold text-[#101828] leading-tight line-clamp-2">{p.name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{p.city}</div>
                    </div>
                  </div>
                  {/* Rows */}
                  <div className="flex flex-col">
                    {rows.map((r) => (
                      <div key={r.key} className="px-3 py-3 border-b border-[#E4E7EC] min-h-[64px] flex flex-col justify-center text-sm text-[#101828]">
                        <div className="lg:hidden text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">{r.label}</div>
                        {r.render(p)}
                      </div>
                    ))}
                    <div className="p-3">
                      <Button className="w-full rounded-full bg-[#D97706] text-white hover:bg-[#b45f05] hover:text-white" onClick={() => book(p)} data-testid={`compare-book-${p.id}`}>
                        Book Now <ArrowRight size={15} className="ml-1.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Add column */}
            {providers.length < 3 && (
              <div className={`${colWidth} shrink-0 grid place-items-center rounded-2xl border-2 border-dashed border-[#CBD5E1] bg-white/50 min-h-[400px] relative`} ref={boxRef}>
                {!adding ? (
                  <button onClick={() => setAdding(true)} data-testid="compare-add-btn" className="flex flex-col items-center gap-2 text-[#0D5C75] font-semibold">
                    <span className="grid place-items-center h-12 w-12 rounded-full bg-[#E9F5F9]"><Plus size={22} /></span>
                    Add Hospital
                  </button>
                ) : (
                  <div className="w-full p-4">
                    <div className="relative">
                      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input autoFocus placeholder="Search hospitals…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="compare-add-search" />
                    </div>
                    <div className="mt-2 max-h-64 overflow-y-auto divide-y divide-[#F1F5F9] rounded-lg border border-[#E4E7EC] bg-white">
                      {candidates.map((c) => (
                        <button key={c.id} onClick={() => addCol(c.id)} data-testid={`compare-add-option-${c.id}`}
                          className="w-full text-left px-3 py-2 hover:bg-[#E9F5F9] text-sm">
                          <div className="font-medium text-[#101828] truncate">{c.name}</div>
                          <div className="text-xs text-muted-foreground">{c.city}</div>
                        </button>
                      ))}
                      {candidates.length === 0 && <div className="px-3 py-4 text-sm text-muted-foreground text-center">No matches</div>}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2 lg:hidden">Swipe horizontally to compare each hospital →</p>
      </div>
    </div>
  );
}
